<script lang="ts" setup>
import { CalendarDaysIcon as CalendarIcon, ChevronLeft, ChevronRight } from '@lucide/vue';

import {
  DatePickerAnchor,
  DatePickerArrow,
  DatePickerCalendar,
  DatePickerCell,
  DatePickerCellTrigger,
  DatePickerContent,
  DatePickerField,
  DatePickerGrid,
  DatePickerGridBody,
  DatePickerGridHead,
  DatePickerGridRow,
  DatePickerHeadCell,
  DatePickerHeader,
  DatePickerHeading,
  DatePickerNext,
  DatePickerPrev,
  DatePickerRoot,
  DatePickerTrigger,
} from 'reka-ui';
import { parseAbsolute } from '@internationalized/date';
import { convertToLocaleDate } from '../../helpers/date.js';

const props = defineProps<{
  minDate?: Date;
  inputId: string;
  placeholder?: string;
}>();

const minDateValue = props.minDate ? parseAbsolute(props.minDate.toISOString(), 'UTC') : undefined;

defineModel<Date>();
</script>

<template>
  <DatePickerRoot
  :model-value="modelValue ? parseAbsolute(modelValue.toISOString(), 'UTC') : undefined"
  :min-value="minDateValue"
  :prevent-deselect="true" :locale="'nl'" 
  :close-on-select="true"
  :id="inputId"
   @update:model-value="(date) => $emit('update:modelValue', date?.toDate('UTC'))">
    <DatePickerField class="datepicker__field" v-slot="{ modelValue }" style="width: 100%">
      <div class="date-picker-field datepicker__field__inner order-form__input-text">
        <input :id="`${inputId}-value`" readonly :value="modelValue ? convertToLocaleDate(modelValue?.toDate('UTC')) : '&nbsp;'" :placeholder="placeholder" />
      </div>

      <DatePickerTrigger class="datepicker__field__trigger">
        <CalendarIcon />
      </DatePickerTrigger>
    </DatePickerField>

    <DatePickerAnchor />

    <DatePickerContent :position="'popper'" :position-strategy="'absolute'" class="datepicker__content">
      <DatePickerArrow class="datepicker__content__arrow" />

      <DatePickerCalendar v-slot="{ weekDays, grid }">
        <DatePickerHeader class="datepicker__content__header">
          <DatePickerPrev>
            <ChevronLeft />
          </DatePickerPrev>
          <DatePickerHeading />
          <DatePickerNext>
            <ChevronRight />
          </DatePickerNext>
        </DatePickerHeader>

        <DatePickerGrid v-for="month in grid" :key="month.value.toString()" class="datepicker__grid">
          <DatePickerGridHead>
            <DatePickerGridRow>
              <DatePickerHeadCell v-for="day in weekDays" :key="day" class="datepicker__grid__cell datepicker__grid__cell--head">
                {{ day }}
              </DatePickerHeadCell>
            </DatePickerGridRow>
          </DatePickerGridHead>

          <DatePickerGridBody>
            <DatePickerGridRow v-for="(weekDates, idx) in month.rows" :key="`weekDate-${idx}`">
              <DatePickerCell
                v-for="weekDate in weekDates"
                :key="weekDate.toString()"
                :date="weekDate"
                class="datepicker__grid__cell datepicker__grid__cell--item"
              >
                <DatePickerCellTrigger :day="weekDate" :month="month.value" />
              </DatePickerCell>
            </DatePickerGridRow>
          </DatePickerGridBody>
        </DatePickerGrid>
      </DatePickerCalendar>
    </DatePickerContent>
  </DatePickerRoot>
</template>

<style scoped></style>
